import { useMemo } from 'react';
import {
  usePaymentTypes,
  useProjectTypes,
  useTaskTypes,
} from '../../hooks/useTemplateTypes';
import type {
  TemplateTrioAnchor,
  TemplateTrioValue,
} from './ProjectFormTemplateSelector';

/**
 * Phase 41 Plan 41-10 (Surface 1) — non-blocking dismissible recommendation
 * banner for the template trio.
 *
 * Per CONTEXT carried-forward Q3: sky-blue, dismissible, NEVER blocks the
 * submit. When the user picks a non-baseline template in any of the three
 * dropdowns, the anchor's row carries `recommended*Id` companions (Phase
 * 35-04 backend); we surface the suggested cross-set values via Apply
 * (sets the other two dropdowns) or Dismiss (hides without changes).
 *
 * The banner DOES NOT render when:
 *   - `anchor` is null (no user pick yet)
 *   - the anchor row carries no recommendations (both companion fields
 *     are null on that row — e.g. baseline templates)
 *   - the recommendation matches the current selection (nothing to suggest)
 *
 * Per CLAUDE.md D-Copy gate: English-only copy.
 */

export interface TrioRecommendationBannerProps {
  anchor: TemplateTrioAnchor | null;
  current: TemplateTrioValue;
  onApply: (next: TemplateTrioValue) => void;
  onDismiss: () => void;
}

export function TrioRecommendationBanner({
  anchor,
  current,
  onApply,
  onDismiss,
}: TrioRecommendationBannerProps) {
  const pay = usePaymentTypes();
  const proj = useProjectTypes();
  const tsk = useTaskTypes();

  const recommended = useMemo<TemplateTrioValue | null>(() => {
    if (!anchor) return null;

    if (anchor === 'payment') {
      const row = (pay.data?.data ?? []).find((r) => r.id === current.paymentTypeId);
      if (!row?.recommendedProjectTypeId && !row?.recommendedTaskTypeId) return null;
      return {
        paymentTypeId: current.paymentTypeId,
        projectTypeId: row?.recommendedProjectTypeId ?? current.projectTypeId,
        taskTypeId: row?.recommendedTaskTypeId ?? current.taskTypeId,
      };
    }

    if (anchor === 'projectType') {
      const row = (proj.data?.data ?? []).find((r) => r.id === current.projectTypeId);
      if (!row?.recommendedPaymentTypeId && !row?.recommendedTaskTypeId) return null;
      return {
        paymentTypeId: row?.recommendedPaymentTypeId ?? current.paymentTypeId,
        projectTypeId: current.projectTypeId,
        taskTypeId: row?.recommendedTaskTypeId ?? current.taskTypeId,
      };
    }

    if (anchor === 'task') {
      const row = (tsk.data?.data ?? []).find((r) => r.id === current.taskTypeId);
      if (!row?.recommendedPaymentTypeId && !row?.recommendedProjectTypeId) return null;
      return {
        paymentTypeId: row?.recommendedPaymentTypeId ?? current.paymentTypeId,
        projectTypeId: row?.recommendedProjectTypeId ?? current.projectTypeId,
        taskTypeId: current.taskTypeId,
      };
    }

    return null;
  }, [anchor, current, pay.data, proj.data, tsk.data]);

  const differs =
    recommended !== null &&
    (recommended.paymentTypeId !== current.paymentTypeId ||
      recommended.projectTypeId !== current.projectTypeId ||
      recommended.taskTypeId !== current.taskTypeId);

  if (!differs || !recommended) return null;

  return (
    <div
      className="px-4 py-3 rounded-md bg-sky-50 border border-sky-200 flex items-center justify-between gap-3 mb-4"
      role="status"
    >
      <div className="text-[12.5px] text-sky-900">
        <span className="font-semibold">Suggested combination:</span>{' '}
        we recommend matching templates for this selection.
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onApply(recommended)}
          className="px-3 py-1.5 rounded-md bg-sky-600 text-white text-[12px] font-semibold hover:bg-sky-700"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="px-3 py-1.5 rounded-md bg-white border border-sky-200 text-sky-700 text-[12px] hover:bg-sky-50"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}
